import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './hooks/useApi';
import { useAuth } from './hooks/useAuth';
import { Layout } from './components/layout/Layout';
import { FullPageSpinner } from './components/ui/Spinner';
import { AdminLayout } from './components/layout/AdminLayout';
import { ErrorBoundary } from './components/ErrorBoundary';

// Eagerly load Dashboard — it's the primary landing page and lazy-loading adds
// an extra round trip that hurts cold-start time for high-latency users.
import Dashboard from './pages/AIDashboard';
const Stock = lazy(() => import('./pages/Dashboard'));
const Products = lazy(() => import('./pages/Products'));
const Channels = lazy(() => import('./pages/Channels'));
const Settings = lazy(() => import('./pages/Settings'));
const Sync = lazy(() => import('./pages/Sync'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const Login = lazy(() => import('./pages/Auth/Login'));
const Register = lazy(() => import('./pages/Auth/Register'));
const ForgotPassword = lazy(() => import('./pages/Auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/Auth/ResetPassword'));

// Legal pages - lazy loaded
const Terms = lazy(() => import('./pages/Legal/Terms'));
const Privacy = lazy(() => import('./pages/Legal/Privacy'));

// Connect pages (marketplace landing) - lazy loaded
const EposnowConnect = lazy(() => import('./pages/Connect/EposnowConnect'));

// 404 page - lazy loaded
const NotFound = lazy(() => import('./pages/NotFound'));

// Founder pages - lazy loaded
const FounderDashboard = lazy(() => import('./pages/Founder/Dashboard'));
const FounderTenants = lazy(() => import('./pages/Founder/Tenants'));
const FounderAlerts = lazy(() => import('./pages/Founder/Alerts'));

// Admin pages - lazy loaded
const AdminDashboard = lazy(() => import('./pages/Admin/Dashboard'));
const AdminTenants = lazy(() => import('./pages/Admin/Tenants'));
const AdminTenantDetail = lazy(() => import('./pages/Admin/TenantDetail'));
const AdminSyncMonitor = lazy(() => import('./pages/Admin/SyncMonitor'));
const AdminSystemHealth = lazy(() => import('./pages/Admin/SystemHealth'));

// Protected Route wrapper
const ProtectedRoute: React.FC = () => {
  const { isAuthenticated, user, isHydrating } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Wait for server hydration before making routing decisions
  // This prevents flashing the wrong page when stale localStorage has incorrect onboardingComplete
  if (isHydrating) {
    return <FullPageSpinner />;
  }

  // Redirect to onboarding if not completed
  if (user && !user.onboardingComplete) {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <Layout>
      <Suspense fallback={<FullPageSpinner />}>
        <Outlet />
      </Suspense>
    </Layout>
  );
};

// Public Route wrapper (redirects to dashboard if already authenticated)
const PublicRoute: React.FC = () => {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <Suspense fallback={<FullPageSpinner />}>
      <Outlet />
    </Suspense>
  );
};

// Onboarding Route wrapper
const OnboardingRoute: React.FC = () => {
  const { isAuthenticated, user, isHydrating } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Wait for server hydration before redirecting away from onboarding
  if (isHydrating) {
    return <FullPageSpinner />;
  }

  if (user?.onboardingComplete) {
    return <Navigate to="/" replace />;
  }

  return (
    <Suspense fallback={<FullPageSpinner />}>
      <Outlet />
    </Suspense>
  );
};

// Admin Route wrapper - checks for super admin status
const AdminRoute: React.FC = () => {
  const { isAuthenticated, user } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!user?.isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  return <AdminLayout />;
};

function App() {
  useEffect(() => {
    // On mount, revalidate the stored auth token and refresh user profile from the backend.
    // This ensures stale localStorage state (e.g. incorrect onboardingComplete) is corrected.
    import('./stores/authStore').then(({ useAuthStore }) => {
      useAuthStore.getState().hydrateFromServer();
    });
  }, []);

  return (
    <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route element={<PublicRoute />}>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
          </Route>

          {/* Legal pages (standalone - no auth wrapper) */}
          <Route
            path="/terms"
            element={
              <Suspense fallback={<FullPageSpinner />}>
                <Terms />
              </Suspense>
            }
          />
          <Route
            path="/privacy"
            element={
              <Suspense fallback={<FullPageSpinner />}>
                <Privacy />
              </Suspense>
            }
          />

          {/* Marketplace connect routes (standalone - no auth wrapper) */}
          <Route
            path="/connect/eposnow"
            element={
              <Suspense fallback={<FullPageSpinner />}>
                <EposnowConnect />
              </Suspense>
            }
          />

          {/* Onboarding route */}
          <Route element={<OnboardingRoute />}>
            <Route path="/onboarding" element={<Onboarding />} />
          </Route>

          {/* Protected routes */}
          <Route element={<ProtectedRoute />}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/stock" element={<Stock />} />
            <Route path="/products" element={<Products />} />
            <Route path="/channels" element={<Channels />} />
            <Route path="/sync" element={<Sync />} />
            <Route path="/settings" element={<Settings />} />
          </Route>

          {/* Admin routes */}
          <Route element={<AdminRoute />}>
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/tenants" element={<AdminTenants />} />
            <Route path="/admin/tenants/:id" element={<AdminTenantDetail />} />
            <Route path="/admin/sync-monitor" element={<AdminSyncMonitor />} />
            <Route path="/admin/system-health" element={<AdminSystemHealth />} />
            <Route path="/admin/founder" element={<FounderDashboard />} />
            <Route path="/admin/founder/tenants" element={<FounderTenants />} />
            <Route path="/admin/founder/alerts" element={<FounderAlerts />} />
          </Route>

          {/* 404 - catch all */}
          <Route
            path="*"
            element={
              <Suspense fallback={<FullPageSpinner />}>
                <NotFound />
              </Suspense>
            }
          />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
